import { useState } from "react";
import { useTranslation } from "react-i18next";

import { Button } from "@/components/ui/button";
import { Modal } from "@/components/ui/modal";
import { toast } from "@/components/ui/sonner";
import { relativeTime } from "@/lib/format/date";
import { cn } from "@/lib/utils/cn";

import { CatalogueWriteError, useReviewFlag } from "../api";
import type { FlagKey, FlagRequest } from "../types";

/**
 * Decide on the flag changes other administrators proposed — the counterpart
 * of the Django review modal.
 *
 * One batch decision over the requests shown: approve them all, or propose
 * other values with a note, which then await yet another administrator. The
 * decision names the requests the reviewer saw, and the server never lets a
 * reviewer act on their own proposal.
 */
export function FlagReviewDialog({
  flag,
  requests,
  onClose,
}: {
  flag: FlagKey;
  /** The pending requests proposed by someone else. */
  requests: FlagRequest[];
  onClose: () => void;
}) {
  const { t, i18n } = useTranslation();
  const review = useReviewFlag(flag);
  const [proposing, setProposing] = useState(false);
  const [values, setValues] = useState<Record<string, boolean>>(() =>
    Object.fromEntries(requests.map((req) => [req.target_id, req.proposed_value])),
  );
  const [note, setNote] = useState("");
  const [error, setError] = useState<string | null>(null);
  const ids = requests.map((req) => req.id);

  const decide = async (decision: "approve" | "propose") => {
    setError(null);
    try {
      const result = await review.mutateAsync(
        decision === "approve"
          ? { decision, request_ids: ids }
          : { decision, request_ids: ids, values, note },
      );
      // The Django review views' flash messages, one per outcome.
      const n = result?.count ?? 0;
      if (decision === "approve") {
        if (n) toast.success(t(`catalogue.review.approved.${flag}`, { n }));
        else toast.info(t("catalogue.review.nothingToApprove"));
      } else if (n) {
        toast.success(t("catalogue.review.countered", { n }));
      } else {
        toast.info(t("catalogue.review.nothingToReview"));
      }
      onClose();
    } catch (err) {
      const noteError =
        err instanceof CatalogueWriteError && err.errors?.note ? t("catalogue.review.noteRequired") : undefined;
      const message = err instanceof CatalogueWriteError ? err.detail : undefined;
      setError(noteError ?? message ?? t("catalogue.error"));
    }
  };

  const onOff = (value: boolean) =>
    value ? t("catalogue.review.enabled") : t("catalogue.review.disabled");

  return (
    <Modal title={t(`catalogue.review.title.${flag}`)} onClose={onClose} className="max-w-3xl">
      <div className="min-h-0 flex-1 space-y-3 overflow-y-auto p-4 text-sm">
        <p className="text-muted-foreground">
          {proposing ? (
            t(`catalogue.review.introPropose.${flag}`)
          ) : (
            <>
              {t("catalogue.review.introReview")}{" "}
              <strong>{t("catalogue.review.introReviewStrong")}</strong>
            </>
          )}
        </p>
        <ul className="divide-y divide-border rounded-md border border-border">
          {requests.map((req) => (
            <li key={req.id} className="flex flex-wrap items-center gap-3 p-3">
              <div className="min-w-0 flex-1">
                <p className="text-xs text-muted-foreground">{req.crumb}</p>
                <p className="font-medium">{req.target_name}</p>
                <p className="mt-1 flex items-center gap-2 text-xs text-muted-foreground">
                  <span
                    aria-hidden
                    className="inline-flex size-6 items-center justify-center rounded-full bg-muted font-semibold text-foreground"
                  >
                    {req.proposer_initials}
                  </span>
                  {t("catalogue.review.proposedBy")}{" "}
                  <b>{req.proposed_by ?? t("catalogue.review.deletedUser")}</b>
                  <span aria-hidden>·</span>
                  {relativeTime(req.created_at, i18n.language)}
                </p>
                {req.note && <p className="mt-1 italic">{req.note}</p>}
              </div>
              <p className="flex items-center gap-2 text-xs">
                <span className="text-muted-foreground line-through">
                  {onOff(req.live_value)}
                </span>
                <span aria-hidden>→</span>
                <b>{onOff(req.proposed_value)}</b>
              </p>
              {proposing && (
                <div
                  role="radiogroup"
                  aria-label={t("catalogue.review.proposedValue")}
                  className="flex overflow-hidden rounded-md border border-border"
                >
                  {[false, true].map((value) => (
                    <button
                      key={String(value)}
                      type="button"
                      role="radio"
                      aria-checked={values[req.target_id] === value}
                      onClick={() =>
                        setValues((current) => ({ ...current, [req.target_id]: value }))
                      }
                      className={cn(
                        "px-3 py-1 text-xs",
                        values[req.target_id] === value
                          ? "bg-primary text-primary-foreground"
                          : "bg-background",
                      )}
                    >
                      {onOff(value)}
                    </button>
                  ))}
                </div>
              )}
            </li>
          ))}
        </ul>
        {proposing && (
          <div className="space-y-1">
            <label htmlFor="flag-review-note" className="font-medium">
              {t("catalogue.review.note")}{" "}
              <span className="text-muted-foreground">— {t("catalogue.review.required")}</span>
            </label>
            <textarea
              id="flag-review-note"
              rows={2}
              value={note}
              onChange={(event) => setNote(event.target.value)}
              placeholder={t("catalogue.review.notePlaceholder")}
              className="w-full rounded-md border border-input bg-background px-3 py-2"
            />
          </div>
        )}
        {error && (
          <p role="alert" className="text-destructive-ink">
            {error}
          </p>
        )}
      </div>
      <div className="flex flex-wrap items-center justify-end gap-2 border-t border-border p-4">
        <span className="mr-auto text-xs text-muted-foreground">
          <b>{requests.length}</b> {t("catalogue.review.proposals")}
        </span>
        <Button variant="outline" onClick={proposing ? () => setProposing(false) : onClose}>
          {proposing ? t("catalogue.cancel") : t("catalogue.review.later")}
        </Button>
        {proposing ? (
          <Button
            onClick={() => void decide("propose")}
            disabled={review.isPending || !note.trim()}
          >
            {t("catalogue.review.submitCounter")}
          </Button>
        ) : (
          <>
            <Button variant="outline" onClick={() => setProposing(true)}>
              {t("catalogue.review.propose")}
            </Button>
            <Button onClick={() => void decide("approve")} disabled={review.isPending}>
              {t("catalogue.review.approveAll")}
            </Button>
          </>
        )}
      </div>
    </Modal>
  );
}
